import { GeneralRecommendationResponse } from 'api-clients/monolith';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { PencilEditIcon } from '@/components/icons/pencil-edit';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { ChannelEdit } from './channel-edit';

import type { GeneralRecommendationForm } from '../types';

interface ChannelSummaryProps {
  channelDetails: GeneralRecommendationResponse['recommended_channels'][number];
  selectedChannels: string[];
  handleChannelSelectionChange: (channelSlug: string) => void;
  hookForm: GeneralRecommendationForm;
  isLast: boolean;
}

export function ChannelSummary({
  channelDetails,
  selectedChannels,
  handleChannelSelectionChange,
  hookForm,
  isLast,
}: ChannelSummaryProps) {
  const { slug: channelSlug, name: channelName } = channelDetails;
  const { formatCurrency } = useCurrencySymbol();

  const [isEditing, setIsEditing] = useState(false);

  const {
    watch,
    formState: { errors },
    trigger,
  } = hookForm;
  const budget = watch(`${channelSlug}.budget`);
  const duration = watch(`${channelSlug}.duration`);

  const hasErrors = !!errors?.[channelSlug];

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    const isValid = await trigger(`${channelSlug}`);
    if (!isValid) return;

    setIsEditing(false);
  };

  const handleCloseClick = () => {
    setIsEditing(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3 p-2">
        <div className="flex justify-between gap-4">
          {/* Channel selection checkbox with channel icon */}
          <Checkbox
            checked={selectedChannels.includes(channelSlug)}
            onCheckedChange={() => handleChannelSelectionChange(channelSlug)}
            label={
              <ChannelIcon
                variant="large"
                size="sm"
                channelSlug={channelSlug}
                channelName={channelName}
              />
            }
          />

          {isEditing ? (
            /* Check and Close buttons */
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="icon"
                className="bg-fx-green hover:bg-fx-green/80 size-6 rounded-sm"
                onClick={() => void handleSaveClick()}
              >
                <Check className="size-3 text-white" />
              </Button>
              <Button
                type="button"
                variant="icon"
                className="bg-fx-rose hover:bg-fx-rose/80 size-6 rounded-sm"
                onClick={handleCloseClick}
              >
                <X className="size-3 text-white" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* Budget and duration tag */}
              <Tag
                text={
                  <>
                    <span className="font-bold">
                      {budget ? formatCurrency(budget) : '-'}
                    </span>{' '}
                    /{' '}
                    {duration ? (
                      <FormattedMessage
                        defaultMessage="{duration, plural, one {{duration} day} other {{duration} days}}"
                        description="Campaign length shown next to currency in the channel row tag"
                        values={{ duration }}
                      />
                    ) : (
                      '-'
                    )}
                  </>
                }
                className="bg-fx-neutral-100"
              />

              {/* Edit button */}
              <Button
                type="button"
                variant="icon"
                size="icon"
                onClick={handleEditClick}
              >
                <PencilEditIcon className="text-fx-neutral-600" />
              </Button>
            </div>
          )}
        </div>

        {isEditing && (
          <ChannelEdit channelSlug={channelSlug} hookForm={hookForm} />
        )}

        {hasErrors && !isEditing && (
          <Typography variant="small1" className="!text-fx-rose -mt-4">
            {errors?.[channelSlug]?.budget?.message ??
              errors?.[channelSlug]?.duration?.message}
          </Typography>
        )}
      </div>

      {!isLast && <div className="bg-fx-neutral-200 h-px w-full" />}
    </>
  );
}
